import { cn } from '../lib/utils';

/**
 * RankRefsCell - Compact DFS rank + referring domains in a two-line cell.
 *
 * Line 1: #12,450 (rank with color: green <10k, yellow <50k, grey >50k)
 * Line 2: ↗ 234 refs (referring domains count)
 *
 * Tooltip: DA | PA | Spam | Traffic | Backlinks
 */
export default function RankRefsCell({
  dfsRank,
  referringDomains,
  da,
  pa,
  spamScore,
  traffic,
  backlinksCount,
  dfsSpamScore,
}) {
  const hasRank = dfsRank != null;
  const hasRefs = referringDomains != null && referringDomains > 0;
  const hasTraffic = traffic != null && traffic > 0;

  if (!hasRank && !hasRefs && !hasTraffic) {
    return <span className="text-xs text-muted-foreground/40">&mdash;</span>;
  }

  // Rank color
  const rankColor = !hasRank
    ? 'text-muted-foreground'
    : dfsRank < 10000
      ? 'text-success'
      : dfsRank < 50000
        ? 'text-warning'
        : 'text-muted-foreground';

  // Format large numbers compactly
  const formatNum = (n) => {
    if (n == null) return '-';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  // Build tooltip with metric explanations
  const tooltipParts = [];
  if (da != null) tooltipParts.push(`DA (Domain Authority, Moz 0-100): ${Math.round(da)}`);
  if (pa != null) tooltipParts.push(`PA (Page Authority, Moz 0-100): ${Math.round(pa)}`);
  if (spamScore != null) tooltipParts.push(`Moz Spam (0-17): ${Math.round(spamScore)}`);
  if (dfsSpamScore != null) tooltipParts.push(`DFS Spam (0-100): ${dfsSpamScore}`);
  if (traffic != null && traffic > 0) tooltipParts.push(`Organic Traffic: ${formatNum(traffic)}/mo`);
  if (backlinksCount != null && backlinksCount > 0) tooltipParts.push(`Total Backlinks: ${formatNum(backlinksCount)}`);

  return (
    <div
      className="flex flex-col gap-0 cursor-default"
      title={tooltipParts.join(' | ')}
    >
      {hasRank && (
        <span className={cn('text-xs font-semibold tabular-nums', rankColor)}>
          #{dfsRank.toLocaleString()}
        </span>
      )}
      {hasRefs && (
        <span className="text-[11px] text-muted-foreground tabular-nums">
          ↗ {formatNum(referringDomains)} refs
        </span>
      )}
      {hasTraffic && (
        <span className="text-[11px] tabular-nums text-blue-400">
          ⚡ {formatNum(traffic)}/mo
        </span>
      )}
    </div>
  );
}
